import type Asset from '../asset';
import { type FilterJSON } from './types';
import FilterOption from './filterOption';
import FileTypeFilter from './fileTypeFilter';
import LabelFilter from './labelFilter';
import RatingFilter from './ratingFilter';
import RoundFilter from './roundFilter';
import UserAssignedFilter from './userAssignedFilter';

export type AssetFilterParams = {
	searchText?: string,
	filterOptions?: FilterOption[]
}

export default class AssetFilters {
	searchText: string;
	filterOptions: FilterOption[];

	constructor( { searchText, filterOptions }: AssetFilterParams ) {
		this.searchText = searchText || '';
		this.filterOptions = filterOptions || [];
	}

	// Meant to be used as a dependency for React (or React-like) hooks
	get stringOfFilterOptionIds() {
		return this.filterOptions.map( option => option.id ).toString();
	}

	get usesSearchTextOrFilterOption() {
		return !!this.searchText || this.filterOptions?.length > 0;
	}

	get isSearching() {
		return !!this.searchText;
	}

	get filterAmount() {
		return this.filterOptions.reduce( ( sum, option ) => sum + option.filterAmount, 0 );
	}

	get isFiltering() {
		return this.filterAmount > 0;
	}

	get isFilteringOrSearching() {
		return this.isFiltering || this.isSearching;
	}

	get filteredLabelIds() {
		return this.labelFilterOption?.ids || [];
	}

	get labelMatchAllOption() {
		return this.labelFilterOption?.matchAll || false;
	}

	get filteredUserAssignedIds() {
		return this.userAssignedFilterOption?.ids || [];
	}

	isFilteringBy( filterOption: FilterOption ) {
		return this.filterOptions.some( option => option.id === filterOption.id );
	}

	filterAssets( assets: Asset[] ) {
		return assets.filter( asset => (
			this.passesSearchFilter( asset )
				&& this.passesFilterOption( asset )
		) );
	}

	toJson() {
		let json: FilterJSON = {};

		if ( this.searchText ) json.name = this.searchText;
		if ( this.filterOptions && this.filterOptions.length > 0 ) {
			json = this.filterOptions.reduce(
				( acc, filterOption ) => ( { ...acc, ...filterOption.toJson() } ), json
			);
		}

		return json;
	}

	toJsonString() {
		return JSON.stringify( this.toJson() );
	}

	private passesSearchFilter( asset: Asset ) {
		return !this.searchText || asset.name.toLowerCase().includes( this.searchText.toLocaleLowerCase() );
	}

	private passesFilterOption( asset: Asset ) {
		return this.filterOptions.every( filterOption => filterOption.isPassedBy( asset ) );
	}

	private get labelFilterOption() {
		return this.filterOptions.find( option => option instanceof LabelFilter ) as LabelFilter;
	}

	private get userAssignedFilterOption() {
		return this.filterOptions.find( option => option instanceof UserAssignedFilter ) as UserAssignedFilter;
	}

	get roundFilterOption() {
		return this.filterOptions.find( option => option instanceof RoundFilter ) as RoundFilter | undefined;
	}

	get isFilteringByRound() {
		return !!this.roundFilterOption;
	}

	get fileTypeFilterOption() {
		return this.filterOptions.find( option => option instanceof FileTypeFilter ) as FileTypeFilter | undefined;
	}

	get isFilteringByFileType() {
		return !!this.fileTypeFilterOption;
	}

	get ratingFilterOption() {
		return this.filterOptions.find( option => option instanceof RatingFilter ) as RatingFilter | undefined;
	}

	get isFilteringByRating() {
		return !!this.ratingFilterOption;
	}

	withImageTypeFilter() {
		if ( this.isFilteringByFileType ) return this;

		return new AssetFilters( {
			searchText: this.searchText,
			filterOptions: [ ...this.filterOptions, new FileTypeFilter( 'image' ) ]
		} );
	}
}
